<div class="flex flex-col items-end gap-1 p-4">
  @if (displayAddressVerificationWarning()) {
    <pr-information-box theme="warn">
      <div class="flex items-center gap-4">
        <span>To lodge an ACC claim, please verify your practice address</span>
        <button mat-flat-button color="warn" (click)="verifyPracticeAddress()">
          Verify Address
        </button>
      </div>
    </pr-information-box>
  }

  <div class="flex items-center gap-4">
    @if (this.patient(); as patient) {
      <pt-loader-button
        type="flat"
        color="primary"
        [loading]="accApi.isLodging()"
        [disabled]="accApi.isLodging() || displayAddressVerificationWarning()"
        (click)="lodgeClaim(patient)"
      >
        Lodge ACC Claim
      </pt-loader-button>
    }

    <button
      mat-flat-button
      color="primary"
      [disabled]="displayAddressVerificationWarning()"
      (click)="accApi.syncRequests(this.patient()?.ref)"
    >
      Sync ACC Requests
    </button>
  </div>
</div>

<div class="rounded-sm shadow-md">
  @if (noClaims$ | async) {
    <pr-empty-state image="list" title="ACC claims" />
  } @else {
    <table
      mat-table
      matSort
      matSortActive="createdAt"
      matSortDirection="desc"
      class="w-full"
      [dataSource]="dataSource"
    >
      <ng-container matColumnDef="status">
        <th *matHeaderCellDef mat-header-cell mat-sort-header class="w-24">
          Status
        </th>
        <td *matCellDef="let data" mat-cell class="w-24">
          <pr-status-label
            class="w-16"
            [label]="data.status"
            [color]="data.status | map: getStatusColour"
            [dense]="true"
          />
        </td>
      </ng-container>

      <ng-container matColumnDef="createdAt">
        <th *matHeaderCellDef mat-header-cell mat-sort-header>Created</th>
        <td *matCellDef="let data" mat-cell>
          {{ data.createdAt | moment | amDateFormat: dateFormat }}
        </td>
      </ng-container>

      <ng-container matColumnDef="claimNumber">
        <th *matHeaderCellDef mat-header-cell mat-sort-header>Claim Number</th>
        <td *matCellDef="let data" mat-cell>
          {{ data.data.claimData.claimNumber }}
          @if (data.data.testMode) {
            <pr-acc-test-chip />
          }
        </td>
      </ng-container>

      <ng-container matColumnDef="declarationDate">
        <th *matHeaderCellDef mat-header-cell mat-sort-header>
          Declaration Date
        </th>
        <td *matCellDef="let data" mat-cell>
          {{
            data.data.claimData.declaration?.providerDeclaration
              | moment
              | amDateFormat: dateFormat
          }}
        </td>
      </ng-container>

      <ng-container matColumnDef="patient">
        <th *matHeaderCellDef mat-header-cell>Patient</th>
        <td *matCellDef="let data" mat-cell>
          <a
            class="text-blue-500"
            [routerLink]="data.patient | map: patientLink : this"
          >
            {{ data.patient.name }}
          </a>
        </td>
      </ng-container>

      <ng-container matColumnDef="coverStatus">
        <th *matHeaderCellDef mat-header-cell>Cover Status</th>
        <td *matCellDef="let data" mat-cell>
          {{ data.data.claimStatus?.coverStatus | map: getCoverStatusLabel }}
        </td>
      </ng-container>

      <ng-container matColumnDef="syncStatus">
        <th *matHeaderCellDef mat-header-cell>Sync Status</th>
        <td *matCellDef="let data" mat-cell>
          <div>
            @if (data.errorMessage) {
              <mat-icon
                class="text-warn-500 opacity-80"
                [matTooltip]="data.errorMessage"
              >
                sync_problem
              </mat-icon>
            } @else if (data.cloudTaskId) {
              <mat-spinner matTooltip="Sync in progress..." [diameter]="20" />
            } @else if (data.syncedAt) {
              <mat-icon
                class="text-green-500 opacity-80"
                [matTooltip]="
                  data.syncedAt | moment | amDateFormat: dateTimeFormat
                "
              >
                published_with_changes
              </mat-icon>
            } @else {
              <mat-icon matTooltip="Not Synced" class="opacity-50">
                sync
              </mat-icon>
            }
          </div>
        </td>
      </ng-container>

      <ng-container matColumnDef="actions">
        <th *matHeaderCellDef mat-header-cell>Actions</th>
        <td *matCellDef="let data" mat-cell>
          <button
            mat-icon-button
            [matMenuTriggerFor]="moreMenu"
            [matMenuTriggerData]="{ claim: data }"
          >
            <mat-icon>more_vert</mat-icon>
          </button>
        </td>
      </ng-container>

      @if (displayedColumns(); as displayedColumns) {
        <tr *matHeaderRowDef="displayedColumns" mat-header-row></tr>
        <tr *matRowDef="let row; columns: displayedColumns" mat-row></tr>
        <tr *matNoDataRow class="mat-row">
          <td class="mat-cell" [attr.colspan]="displayedColumns.length">
            No ACC Claim Requests found.
          </td>
        </tr>
      }
    </table>

    <mat-paginator
      #paginator
      showFirstLastButtons
      [length]="dataSource.data.length"
      [pageSize]="pageSize"
      [pageSizeOptions]="pageSizeOptions"
    />

    <mat-menu #moreMenu="matMenu">
      <ng-template let-claim="claim" matMenuContent>
        <button mat-menu-item (click)="viewClaimDetails(claim)">
          <mat-icon fontSet="material-symbols-outlined" class="opacity-80">
            table_eye
          </mat-icon>
          <span>View Claim Details</span>
        </button>

        <button mat-menu-item (click)="viewClaimLodgement(claim)">
          <mat-icon fontSet="material-symbols-outlined" class="opacity-80">
            assignment
          </mat-icon>
          <span>View Lodgement Form</span>
        </button>

        <button
          mat-menu-item
          [disabled]="!claim.data.claimData.claimNumber"
          (click)="accClaimService.addDocuments(claim)"
        >
          <mat-icon fontSet="material-symbols-outlined" class="opacity-80">
            picture_as_pdf
          </mat-icon>
          <span>Add Documents</span>
        </button>

        <button
          mat-menu-item
          [disabled]="!claim.data.documents?.length"
          (click)="accClaimService.viewDocuments(claim)"
        >
          <mat-icon fontSet="material-symbols-outlined" class="opacity-80">
            find_in_page
          </mat-icon>
          <span>View Documents</span>
        </button>

        <button
          mat-menu-item
          [disabled]="claim | map: deleteDisabled : this"
          (click)="accClaimService.deleteClaim(claim)"
        >
          <mat-icon
            fontSet="material-symbols-outlined"
            class="!text-warn-500 opacity-80"
          >
            delete
          </mat-icon>
          <span>Delete Claim</span>
        </button>
      </ng-template>
    </mat-menu>
  }
</div>
